import { useCampaignAnalysisList } from '@/features/campaign/hooks/query';
import type { CampaignAnalysisHistoryItem } from '@/entities/campaign/generated/campaign-type';
import { useMemo, useState } from 'react';

import { mergeCampaignAnalysisHistoryPages } from '../helpers/analysis-history';

type ExplicitSelection = {
  campaignId: string;
  runId: string;
  snapshot?: CampaignAnalysisHistoryItem;
};

export function useCampaignAnalysisHistory(campaignId: string) {
  const historyQuery = useCampaignAnalysisList(campaignId);
  const [explicitSelection, setExplicitSelection] = useState<ExplicitSelection | null>(null);
  const runs = useMemo(
    () => mergeCampaignAnalysisHistoryPages(historyQuery.data?.pages),
    [historyQuery.data?.pages],
  );
  const currentExplicitSelection =
    explicitSelection?.campaignId === campaignId ? explicitSelection : null;
  const explicitlySelectedRunId = currentExplicitSelection?.runId ?? null;
  const selectedRunId = explicitlySelectedRunId ?? runs.at(0)?.run_id ?? null;
  const selectedRun =
    runs.find((run) => run.run_id === selectedRunId) ??
    currentExplicitSelection?.snapshot ??
    runs.at(0) ??
    null;

  const selectRun = (runId: string) => {
    const run = runs.find((historyRun) => historyRun.run_id === runId);
    setExplicitSelection({
      campaignId,
      runId,
      ...(run ? { snapshot: run } : {}),
    });
  };

  const clearSelection = () => {
    setExplicitSelection(null);
  };

  return {
    ...historyQuery,
    clearSelection,
    runs,
    selectedRun,
    selectedRunId,
    selectRun,
  };
}
